import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Theme = "light" | "dark";

interface ThemeState {
  theme: Theme;
  toggle: () => void;
  setTheme: (t: Theme) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: "light",
      toggle: () => set({ theme: get().theme === "light" ? "dark" : "light" }),
      setTheme: (theme) => set({ theme }),
    }),
    { name: "theme" }
  )
);

// CSS variables for each theme — applied to <html> by AppShell.
// v3 "liquid glass" (jul 2026): alineado con la landing v5 — papel frío neutro,
// tinta casi-negra, vidrio con borde especular, sombras neutras (sin tinte de
// color), un solo acento (el de la org). Sin gradientes de fondo.
export const THEME_VARS: Record<Theme, Record<string, string>> = {
  light: {
    "--clr-bg":              "#e7f1f4",
    "--clr-bg-grad":         "#e7f1f4",
    "--clr-sidebar":         "rgba(255,255,255,0.68)",
    "--clr-sidebar-border":  "rgba(255,255,255,0.80)",
    "--clr-topbar":          "rgba(255,255,255,0.62)",
    "--clr-topbar-border":   "rgba(255,255,255,0.75)",
    "--clr-surface":         "rgba(255,255,255,0.68)",
    "--clr-surface2":        "rgba(255,255,255,0.48)",
    "--clr-border":          "rgba(255,255,255,0.85)",
    "--clr-border2":         "rgba(11,18,21,0.09)",
    "--clr-shadow":          "0 8px 40px -12px rgba(11,40,50,0.14), inset 0 1px 0 rgba(255,255,255,0.9)",
    "--clr-shadow-sm":       "0 2px 12px -4px rgba(11,40,50,0.10), inset 0 1px 0 rgba(255,255,255,0.8)",
    "--clr-blur":            "blur(24px) saturate(1.8)",
    "--clr-text":            "#0b1215",
    "--clr-text2":           "rgba(11,18,21,0.68)",
    "--clr-text3":           "rgba(11,18,21,0.48)",
    "--clr-text4":           "rgba(11,18,21,0.34)",
    "--clr-nav-text":        "rgba(11,18,21,0.60)",
    "--clr-nav-active":      "#0b1215",
    "--clr-nav-pill":        "rgba(255,255,255,0.85)",
    "--clr-hover":           "rgba(255,255,255,0.45)",
    "--clr-modal-bg":        "rgba(255,255,255,0.88)",
    "--clr-modal-solid":     "#f7fafb",
    "--clr-modal-solid2":    "#eef3f5",
    "--clr-input-bg":        "rgba(255,255,255,0.75)",
    "--clr-option-bg":       "#f2f6f7",
    "--clr-toggle-bg":       "rgba(255,255,255,0.55)",
    "--clr-toggle-color":    "rgba(11,18,21,0.55)",
    "--clr-orb1":            "rgba(8,145,178,0.16)",
    "--clr-orb2":            "rgba(124,58,237,0.08)",
    "--clr-logo-bg":         "transparent",
    "--clr-logo-radius":     "0px",
    "--clr-logo-padding":    "0px",
    "--clr-logo-text":       "var(--clr-text4)",
    "--clr-logo-filter":     "none",
  },
  dark: {
    "--clr-bg":              "#0c1418",
    "--clr-bg-grad":         "#0c1418",
    "--clr-sidebar":         "rgba(16,23,27,0.72)",
    "--clr-sidebar-border":  "rgba(255,255,255,0.09)",
    "--clr-topbar":          "rgba(16,23,27,0.60)",
    "--clr-topbar-border":   "rgba(255,255,255,0.08)",
    "--clr-surface":         "rgba(255,255,255,0.08)",
    "--clr-surface2":        "rgba(255,255,255,0.05)",
    "--clr-border":          "rgba(255,255,255,0.12)",
    "--clr-border2":         "rgba(255,255,255,0.06)",
    "--clr-shadow":          "0 8px 40px -12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.09)",
    "--clr-shadow-sm":       "0 2px 12px -4px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.07)",
    "--clr-blur":            "blur(24px) saturate(1.5)",
    "--clr-text":            "#eef3f4",
    "--clr-text2":           "rgba(238,243,244,0.72)",
    "--clr-text3":           "rgba(238,243,244,0.50)",
    "--clr-text4":           "rgba(238,243,244,0.32)",
    "--clr-nav-text":        "rgba(255,255,255,0.52)",
    "--clr-nav-active":      "#ffffff",
    "--clr-nav-pill":        "rgba(255,255,255,0.10)",
    "--clr-hover":           "rgba(255,255,255,0.05)",
    "--clr-modal-bg":        "rgba(16,23,27,0.88)",
    "--clr-modal-solid":     "#131b20",
    "--clr-modal-solid2":    "#0e1519",
    "--clr-input-bg":        "rgba(255,255,255,0.07)",
    "--clr-option-bg":       "#131b20",
    "--clr-toggle-bg":       "rgba(255,255,255,0.08)",
    "--clr-toggle-color":    "rgba(255,255,255,0.55)",
    "--clr-orb1":            "rgba(8,145,178,0.14)",
    "--clr-orb2":            "rgba(124,58,237,0.08)",
    // Oscuro: sin caja blanca — invert+hue-rotate invierte la luminosidad
    // (texto oscuro → claro) conservando los tonos del icono.
    "--clr-logo-bg":         "transparent",
    "--clr-logo-radius":     "0px",
    "--clr-logo-padding":    "0px",
    "--clr-logo-text":       "var(--clr-text4)",
    "--clr-logo-filter":     "invert(1) hue-rotate(180deg)",
  },
};
